import { useEffect, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'

import {
  activateForceUpdate,
  addWhiteVersion,
  deleteWhiteVersion,
  listWhiteVersions,
} from '../../../api/applications.js'

export default function ApplicationForceUpdaterPage() {
  const { id } = useParams()
  const { application, onApplicationUpdated } = useOutletContext()
  const [whiteVersions, setWhiteVersions] = useState([])
  const [status, setStatus] = useState({ list: 'idle', activate: 'idle', add: 'idle' })
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')
  const [bundleVersion, setBundleVersion] = useState('')

  const isForceUpdateEnabled = application?.forceUpdateEnabled === true
  const canActivate = !isForceUpdateEnabled

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setStatus((s) => ({ ...s, list: 'loading' }))
    setError(null)
    listWhiteVersions(id)
      .then((list) => {
        if (!cancelled) {
          setWhiteVersions(list)
          setStatus((s) => ({ ...s, list: 'ok' }))
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message ?? String(err))
          setStatus((s) => ({ ...s, list: 'error' }))
        }
      })
    return () => { cancelled = true }
  }, [id])

  async function handleActivate() {
    if (!id || !canActivate || status.activate === 'loading') return
    setError(null)
    setSuccessMessage('')
    setStatus((s) => ({ ...s, activate: 'loading' }))
    try {
      const updated = await activateForceUpdate(id)
      onApplicationUpdated?.(updated)
      setSuccessMessage('Force updater activated successfully.')
      setStatus((s) => ({ ...s, activate: 'idle' }))
    } catch (err) {
      setError(err?.message ?? String(err))
      setStatus((s) => ({ ...s, activate: 'idle' }))
    }
  }

  async function handleAddWhiteVersion(e) {
    e.preventDefault()
    const version = bundleVersion.trim()
    if (!id || !version || status.add === 'loading') return
    setError(null)
    setStatus((s) => ({ ...s, add: 'loading' }))
    try {
      const item = await addWhiteVersion(id, version)
      setWhiteVersions((prev) => [item, ...prev])
      setBundleVersion('')
      setStatus((s) => ({ ...s, add: 'idle' }))
    } catch (err) {
      setError(err?.message ?? String(err))
      setStatus((s) => ({ ...s, add: 'idle' }))
    }
  }

  async function handleDelete(rcId) {
    if (!id || !rcId) return
    setError(null)
    try {
      await deleteWhiteVersion(id, rcId)
      setWhiteVersions((prev) => prev.filter((v) => v.rcId !== rcId))
    } catch (err) {
      setError(err?.message ?? String(err))
    }
  }

  const formatDate = (iso) => {
    if (!iso) return '—'
    try {
      const d = new Date(iso)
      return d.toLocaleString()
    } catch {
      return iso
    }
  }

  return (
    <section className="card sectionCard">
      <div className="cardTitle">Force Updater</div>

      {isForceUpdateEnabled && (
        <div className="validatorStatus validatorStatusActive" role="status">
          <span className="validatorStatusBadge">Force update enabled</span>
        </div>
      )}

      {canActivate && (
        <form onSubmit={(e) => { e.preventDefault(); handleActivate(); }} className="validatorForm">
          {successMessage && (
            <p className="settingsSuccess" role="status">
              {successMessage}
            </p>
          )}
          <div className="formActions">
            <button
              type="submit"
              className="button buttonPrimary"
              disabled={status.activate === 'loading'}
            >
              {status.activate === 'loading' ? 'Activating…' : 'Activate force updater'}
            </button>
          </div>
        </form>
      )}

      <div className="forceUpdaterAddSection">
        <form onSubmit={handleAddWhiteVersion} className="forceUpdaterAddForm">
          <label htmlFor="force-updater-bundle-version" className="formLabel">
            Add White Version
          </label>
          <div className="forceUpdaterAddRow">
            <input
              id="force-updater-bundle-version"
              type="text"
              className="textInput"
              placeholder="e.g. 1.0.0"
              value={bundleVersion}
              onChange={(e) => setBundleVersion(e.target.value)}
              disabled={status.add === 'loading'}
            />
            <button
              type="submit"
              className="button buttonPrimary"
              disabled={!bundleVersion.trim() || status.add === 'loading'}
            >
              {status.add === 'loading' ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <p className="formError" role="alert">
          {error}
        </p>
      )}

      <table className="table" aria-label="White versions">
        <thead>
          <tr>
            <th>Bundle version</th>
            <th>Created</th>
            <th className="alignRight">Actions</th>
          </tr>
        </thead>
        <tbody>
          {status.list === 'loading' && (
            <tr>
              <td colSpan={3} className="muted">
                Loading…
              </td>
            </tr>
          )}
          {status.list === 'ok' && whiteVersions.length === 0 && (
            <tr>
              <td colSpan={3} className="muted">
                No white versions yet.
              </td>
            </tr>
          )}
          {status.list === 'ok' &&
            whiteVersions.map((v) => (
              <tr key={v.rcId || v.id || v.bundleVersion}>
                <td>{v.bundleVersion}</td>
                <td>{formatDate(v.createdAt)}</td>
                <td className="alignRight">
                  <button
                    type="button"
                    className="button buttonGhost buttonDanger"
                    onClick={() => handleDelete(v.rcId)}
                    disabled
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </section>
  )
}
